import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CloudUpload, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { useReportUser } from "@/hooks/useReportUser";
import { ReportedContentReasonEnum } from "@/__generated__/types";
import { toast } from "sonner";

const REASON_OPTIONS = [
  {
    value: ReportedContentReasonEnum.FakeAccount,
    translationKey: "reportContent.reportUser.dialog.reasonOption1",
  },
  {
    value: ReportedContentReasonEnum.Bullying,
    translationKey: "reportContent.reportUser.dialog.reasonOption2",
  },
  {
    value: ReportedContentReasonEnum.Stalking,
    translationKey: "reportContent.reportUser.dialog.reasonOption3",
  },
  {
    value: ReportedContentReasonEnum.PostingInappropriate,
    translationKey: "reportContent.reportUser.dialog.reasonOption4",
  },
  {
    value: ReportedContentReasonEnum.PostingSpam,
    translationKey: "reportContent.reportUser.dialog.reasonOption5",
  },
  {
    value: ReportedContentReasonEnum.SomethingElse,
    translationKey: "reportContent.reportUser.dialog.reasonOption6",
  },
];

interface ReportUserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

export const ReportUser: React.FC<ReportUserProps> = ({
  open,
  onOpenChange,
  userId,
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] =
    useState<ReportedContentReasonEnum>(ReportedContentReasonEnum.Bullying);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);

  const resetForm = () => {
    setSelectedReason(ReportedContentReasonEnum.Bullying);
    setUploadedImage(null);
    setIsDragOver(false);
  };

  const getSelectedReasonText = (reason: ReportedContentReasonEnum): string => {
    const option = REASON_OPTIONS.find((opt) => opt.value === reason);
    return option
      ? t(option.translationKey)
      : t("reportContent.reportUser.dialog.reasonOption2");
  };

  const [reportUser, { loading }] = useReportUser({
    onCompleted: () => {
      toast.success(t("reportContent.reportUser.toast.title"), {
        description: t("reportContent.reportUser.toast.message"),
      });
      resetForm();
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t("reportContent.reportUser.toast.errorTitle"), {
        description: t("reportContent.reportUser.toast.errorMessage"),
      });
    },
  });

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEvent = (
    event: React.DragEvent<HTMLDivElement>,
    type: "over" | "leave" | "drop",
  ) => {
    event.preventDefault();

    if (type === "over") {
      setIsDragOver(true);
    } else if (type === "leave") {
      setIsDragOver(false);
    } else if (type === "drop") {
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        setUploadedImage(file);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setUploadedImage(file);
    }
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!uploadedImage) return;

    await reportUser(userId, selectedReason, uploadedImage);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg leading-none font-semibold text-foreground">
            {t("reportContent.reportUser.dialog.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("reportContent.reportUser.dialog.formAria")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-8">
          {/* File Upload Section */}
          <div>
            <p className="overflow-hidden text-sm leading-5 font-normal text-ellipsis text-foreground">
              {t("reportContent.reportUser.dialog.uploadTitle")}
            </p>

            <div
              className={`mt-2 bg-secondary p-8 text-center shadow-sm transition-colors ${
                isDragOver
                  ? "border-2 border-solid border-blue-400 bg-blue-50"
                  : ""
              }`}
              style={{
                borderRadius: "var(--radius-sm)",
              }}
              onDrop={(e) => handleDragEvent(e, "drop")}
              onDragOver={(e) => handleDragEvent(e, "over")}
              onDragLeave={(e) => handleDragEvent(e, "leave")}
            >
              <div className="flex flex-col items-center space-y-3">
                <CloudUpload className="h-8 w-8 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    {t("reportContent.reportUser.dialog.uploadContentTitle")}
                  </p>
                  <label className="cursor-pointer text-xs leading-5 font-medium text-blue-600 underline hover:text-blue-700">
                    {t("reportContent.reportUser.dialog.uploadContentSubtitle")}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>
            </div>

            {uploadedImage && (
              <p className="pt-2 text-sm text-muted-foreground">
                {t("reportContent.reportUser.dialog.fileAttached", {
                  image: uploadedImage.name,
                })}
              </p>
            )}
          </div>

          {/* Reason Selection */}
          <div className="mt-8">
            <p className="text-sm leading-none font-medium text-foreground">
              {t("reportContent.reportUser.dialog.reasonTitle")}
            </p>

            <div className="mt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-left text-foreground hover:bg-accent">
                    <span className="overflow-hidden text-sm leading-5 font-normal text-ellipsis text-foreground">
                      {getSelectedReasonText(selectedReason)}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full">
                  {REASON_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => setSelectedReason(option.value)}
                    >
                      {t(option.translationKey)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-12 gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="bg-secondary text-secondary-foreground"
          >
            {t("global.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!uploadedImage || loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading
              ? t("reportContent.reportUser.dialog.sending")
              : t("reportContent.reportUser.dialog.sendButtonLabel")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
